/**
 * Generalized wallet-verification flow hook for the YaaS Client Console.
 *
 * Drives the `AccountOwnershipVerificationPanel` step progression while
 * delegating the actual wallet interaction to `useWalletVerification`.
 * Each call to `startFlow` specifies the method explicitly so the UI
 * can show a micro-transfer cost disclosure between sign-message
 * failure and the fallback attempt.
 *
 * Step progression:
 *   signMessage:    walletSign → committing → success
 *                   walletSign → error (→ costDisclosure → walletSign w/microTransfer)
 *   microTransfer:  walletSign → confirming → committing → success
 *   hardware skip:  costDisclosure → confirming → committing → success
 *                   (via `beginMicroTransfer`, bypassing signMessage)
 *
 * Panel-visible state is held in a `useReducer` so every transition is a
 * named action and the related fields (step / error / errorKind / proof)
 * move together atomically. The async machinery — the run token, the
 * pending-wait timer, and the last-run params used by `retry()` — stays in
 * refs, since those are read inside async continuations where reducer
 * state would be stale.
 */

import type {
    VerificationErrorKind,
    VerificationMethod,
    VerificationStep
} from "@solomon-apps/shared/src/components/console/AccountOwnershipVerificationPanel";
import { useCallback, useReducer, useRef } from "react";

import { useWalletVerification, type VerificationProof } from "./useWalletVerification";
import {
    resolveRunOutcome,
    type RunOutcome,
    type RunResolution,
    WALLET_RESPONSE_TIMEOUT_MS
} from "./verifyFlowOutcome";

export type { VerificationErrorKind, VerificationMethod, VerificationStep };

export interface StartFlowArgs {
    address: string;
    messageToSign: string;
    method: VerificationMethod;
    microTransferMemo?: string;
    /**
     * Optional commit step run while the panel shows "committing": receives
     * the proof and performs the mutation the signature authorizes (e.g. the
     * PATCH that writes a new rewards destination). The flow advances to
     * "success" only after this resolves — so the success copy is truthful —
     * and it runs exactly once per run (no effect-retrigger double-fires).
     * On rejection the flow resets to idle; the callback owns surfacing the
     * error to the user (the panel's error step is sign/transfer framing,
     * which doesn't fit a failed API write).
     */
    commit?: (proof: VerificationProof) => Promise<void>;
}

export interface UseVerifyActionFlowResult {
    startFlow: (args: StartFlowArgs) => void;
    /**
     * Skip the signMessage attempt and go straight to the micro-transfer
     * cost disclosure. For wallets that can't sign messages (hardware
     * wallets like Ledger), so the user never hits a failure first.
     * Cancelling the disclosure returns the flow to idle.
     */
    beginMicroTransfer: (args: { address: string; messageToSign: string; commit?: StartFlowArgs["commit"] }) => void;
    step: VerificationStep | null;
    verificationMethod: VerificationMethod | null;
    error: string | null;
    /** Why the flow landed on the error step (null otherwise). */
    errorKind: VerificationErrorKind | null;
    /** Literal message the wallet is being asked to sign. */
    messageToSign: string | null;
    /** True when a flow is active (step !== null). */
    isActive: boolean;
    /** The verification proof returned on success. Available once step
     *  reaches "committing". */
    proof: VerificationProof | null;
    retry: () => void;
    /**
     * Transition from `error` step to `costDisclosure`. Surfaces the
     * micro-transfer cost question inline in the panel; the user
     * confirms via `confirmMicroTransfer` or backs out via
     * `cancelCostDisclosure`.
     */
    tryMicroTransfer: () => void;
    /**
     * Run the flow with method=microTransfer. Called from the
     * `costDisclosure` step when the user confirms the cost.
     */
    confirmMicroTransfer: () => void;
    /**
     * Leave the `costDisclosure` step. Returns to the `error` step when
     * the disclosure was reached from a failed signMessage, or to idle
     * when it was reached directly (hardware affordance).
     */
    cancelCostDisclosure: () => void;
    /** Reset the flow to idle (step = null). */
    cancel: () => void;
}

export interface UseVerifyActionFlowOptions {
    /**
     * When true, a failed signMessage attempt that the wallet *couldn't
     * produce* (errorKind `couldNotSign` — hardware wallets like Ledger, or a
     * non-rejection software failure) auto-advances straight into the
     * micro-transfer run: no `error` step, no manual "use micro-transfer"
     * click, no cost-disclosure. The micro-transfer's own wallet-approval
     * prompt is the user's gate.
     *
     * Deliberate user rejections (`cancelled`) and dead/silent wallets
     * (`noResponse`) are intentionally NOT auto-fallen-back: a user who can
     * sign but declined should retry signing rather than be pushed into a
     * paid transfer, and an unresponsive wallet won't answer a transfer
     * prompt either (and may still have the sign prompt open).
     *
     * Default false — the manual `tryMicroTransfer` → `costDisclosure` →
     * `confirmMicroTransfer` path is preserved for callers that don't opt in.
     */
    autoMicroTransferFallback?: boolean;
}

const COMMITTING_DISPLAY_MS = 500;

/* ── Panel-visible state machine ─────────────────────────────── */

interface FlowState {
    step: VerificationStep | null;
    method: VerificationMethod | null;
    error: string | null;
    errorKind: VerificationErrorKind | null;
    messageToSign: string | null;
    proof: VerificationProof | null;
}

const IDLE_STATE: FlowState = {
    step: null,
    method: null,
    error: null,
    errorKind: null,
    messageToSign: null,
    proof: null
};

type FlowAction =
    /** A run began: fresh walletSign state for the given method. */
    | { type: "runStarted"; method: VerificationMethod; messageToSign: string }
    /** Micro-transfer broadcast; waiting for on-chain confirmation. */
    | { type: "confirming" }
    /** Terminal outcome resolved → committing (success) or error. Sets the
     *  step/error/errorKind/proof quartet atomically so they can't desync. */
    | { type: "resolved"; resolution: RunResolution }
    /** committing → success after the brief display pause. */
    | { type: "succeeded" }
    /** Show the micro-transfer cost disclosure (optionally switching method
     *  for the hardware-affordance entry). Preserves any existing error so a
     *  later `backToError` can restore it. */
    | { type: "costDisclosure"; method?: VerificationMethod; messageToSign?: string }
    /** Return from the cost disclosure to the error step. */
    | { type: "backToError" }
    /** Reset everything to idle. */
    | { type: "reset" };

function flowReducer(state: FlowState, action: FlowAction): FlowState {
    switch (action.type) {
        case "runStarted":
            return {
                step: "walletSign",
                method: action.method,
                messageToSign: action.messageToSign,
                error: null,
                errorKind: null,
                proof: null
            };
        case "confirming":
            return { ...state, step: "confirming" };
        case "resolved":
            return {
                ...state,
                step: action.resolution.step,
                error: action.resolution.error,
                errorKind: action.resolution.errorKind,
                proof: action.resolution.proof
            };
        case "succeeded":
            return { ...state, step: "success" };
        case "costDisclosure":
            return {
                ...state,
                step: "costDisclosure",
                method: action.method ?? state.method,
                messageToSign: action.messageToSign ?? state.messageToSign
            };
        case "backToError":
            return { ...state, step: "error" };
        case "reset":
            return IDLE_STATE;
        default:
            return state;
    }
}

export function useVerifyActionFlow(options?: UseVerifyActionFlowOptions): UseVerifyActionFlowResult {
    const [state, dispatch] = useReducer(flowReducer, IDLE_STATE);

    // Read inside `run`'s async continuation (after awaits), so it lives in a
    // ref that tracks the latest prop rather than in `run`'s dep array.
    const autoFallbackRef = useRef<boolean>(false);
    autoFallbackRef.current = options?.autoMicroTransferFallback ?? false;
    // Stable self-reference: lets the auto-fallback inside `run` start a fresh
    // micro-transfer run without `run` depending on itself. Always points at
    // the latest `run`; only read after awaits, by which time it's assigned.
    const runRef = useRef<((args: StartFlowArgs) => Promise<void>) | null>(null);

    // Last-run args, replayed verbatim by retry(). confirmMicroTransfer keeps
    // this in sync with the method switch so a retry after a failed transfer
    // re-runs the transfer, not the original signMessage params.
    const paramsRef = useRef<StartFlowArgs | null>(null);
    // Whether the cost disclosure was reached from a failed signMessage
    // (→ "Cancel" returns to the error step) versus directly via the
    // hardware affordance (→ "Cancel" returns to idle).
    const disclosureFromErrorRef = useRef<boolean>(false);
    // Monotonic token identifying the active run. Bumped on every start,
    // retry, timeout, and cancel so a stale/zombie verify() promise — e.g.
    // a wallet that finally answers long after we've timed out — can never
    // clobber fresh state. Each async continuation bails unless it still
    // owns the latest id.
    const runIdRef = useRef<number>(0);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const walletVerification = useWalletVerification();

    const clearTimer = useCallback((): void => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const resetToIdle = useCallback((): void => {
        // Bump the run token so any in-flight verify() continuation bails,
        // then clear the pending-wait timeout and reset to idle.
        runIdRef.current += 1;
        clearTimer();
        dispatch({ type: "reset" });
        paramsRef.current = null;
    }, [clearTimer]);

    const run = useCallback(
        async (args: StartFlowArgs): Promise<void> => {
            const myRunId = runIdRef.current + 1;
            runIdRef.current = myRunId;

            clearTimer();
            dispatch({ type: "runStarted", method: args.method, messageToSign: args.messageToSign });

            // Safety net: a wallet that never answers leaves verify()'s
            // signMessage promise pending forever, freezing the panel on
            // "Waiting on wallet…". Time the wait out into a retryable
            // error. Disarmed once a micro-transfer reaches on-chain
            // confirmation, which is bounded by its own blockhash window.
            timerRef.current = setTimeout(() => {
                if (runIdRef.current !== myRunId) return;
                timerRef.current = null;
                if (args.method === "signMessage") {
                    // Invalidate the in-flight verify() promise so a late
                    // wallet response can't overwrite the error we set here.
                    // A late signature costs nothing to redo.
                    runIdRef.current = myRunId + 1;
                }
                // Micro-transfer runs stay subscribed: an approval that
                // lands after this error broadcasts a real, PAID transfer.
                // The continuation below still owns the token, so a late
                // confirmation revives the flow into committing → success
                // instead of silently discarding the user's lamports.
                dispatch({ type: "resolved", resolution: resolveRunOutcome({ kind: "timedout" }, args.method) });
            }, WALLET_RESPONSE_TIMEOUT_MS);

            const result = await walletVerification.verify(
                args.address,
                args.messageToSign,
                { method: args.method, microTransferMemo: args.microTransferMemo },
                undefined,
                // onConfirming — micro-transfer broadcast; hand off to the
                // on-chain confirmation wait and disarm the wallet timeout.
                () => {
                    clearTimer();
                    if (runIdRef.current === myRunId) dispatch({ type: "confirming" });
                }
            );

            if (runIdRef.current !== myRunId) return;
            clearTimer();

            const outcome: RunOutcome = result.proof
                ? { kind: "signed", proof: result.proof }
                : {
                      kind: "failed",
                      reason: result.reason ?? undefined,
                      transactionSignature: result.transactionSignature
                  };
            const resolution = resolveRunOutcome(outcome, args.method);

            // Auto micro-transfer fallback (opt-in): a signMessage the wallet
            // couldn't produce advances straight into the micro-transfer run —
            // no error step, no manual click, no cost disclosure. We dispatch
            // nothing for the failed sign (so the panel never flashes an
            // error) and let the fresh run drive walletSign → confirming.
            // `couldNotSign` only: deliberate cancels and dead wallets fall
            // through to the normal error step below.
            if (autoFallbackRef.current && args.method === "signMessage" && resolution.errorKind === "couldNotSign") {
                const microParams: StartFlowArgs = { ...args, method: "microTransfer" };
                paramsRef.current = microParams;
                void runRef.current?.(microParams);
                return;
            }

            dispatch({ type: "resolved", resolution });

            if (resolution.step !== "committing" || !result.proof) return;

            // Brief pause so the user sees the committing state before
            // success; the caller's commit (when present) runs alongside it
            // and gates the success step on the mutation actually landing.
            const pause = new Promise<void>((resolve) => setTimeout(resolve, COMMITTING_DISPLAY_MS));
            if (args.commit) {
                try {
                    await Promise.all([args.commit(result.proof), pause]);
                } catch {
                    // The commit callback owns user-facing error surfacing;
                    // drop the panel back to idle so that surface is visible.
                    if (runIdRef.current === myRunId) resetToIdle();
                    return;
                }
            } else {
                await pause;
            }
            if (runIdRef.current !== myRunId) return;

            dispatch({ type: "succeeded" });
        },
        [walletVerification, clearTimer, resetToIdle]
    );
    runRef.current = run;

    const startFlow = useCallback(
        (args: StartFlowArgs): void => {
            paramsRef.current = args;
            void run(args);
        },
        [run]
    );

    const beginMicroTransfer = useCallback(
        (args: { address: string; messageToSign: string; commit?: StartFlowArgs["commit"] }): void => {
            const microParams: StartFlowArgs = { ...args, method: "microTransfer" };
            paramsRef.current = microParams;
            disclosureFromErrorRef.current = false;
            // Orphan any in-flight run (same as resetToIdle) so a wallet
            // that answers a previous signMessage prompt late can't clobber
            // the cost-disclosure state we're about to show.
            runIdRef.current += 1;
            clearTimer();
            dispatch({ type: "costDisclosure", method: "microTransfer", messageToSign: args.messageToSign });
        },
        [clearTimer]
    );

    const retry = useCallback((): void => {
        const p = paramsRef.current;
        if (!p) return;
        void run(p);
    }, [run]);

    const tryMicroTransfer = useCallback((): void => {
        // From error → costDisclosure. The actual micro-transfer flow
        // doesn't start until `confirmMicroTransfer`.
        disclosureFromErrorRef.current = true;
        dispatch({ type: "costDisclosure" });
    }, []);

    const confirmMicroTransfer = useCallback((): void => {
        const p = paramsRef.current;
        if (!p) return;
        // Persist the method switch so a later retry() (e.g. after a failed
        // transfer) re-runs the micro-transfer rather than the original
        // signMessage params. Critical for hardware wallets, where retrying
        // signMessage sends the user back into a path that can't work.
        const microParams: StartFlowArgs = { ...p, method: "microTransfer" };
        paramsRef.current = microParams;
        void run(microParams);
    }, [run]);

    const cancelCostDisclosure = useCallback((): void => {
        // From a failed signMessage, return to error so the user can pick
        // again (don't reset params — they may still want to retry
        // signMessage). From the hardware affordance, there's nothing to
        // return to, so back out to idle.
        if (disclosureFromErrorRef.current) {
            dispatch({ type: "backToError" });
        } else {
            resetToIdle();
        }
    }, [resetToIdle]);

    const cancel = useCallback((): void => {
        resetToIdle();
    }, [resetToIdle]);

    return {
        startFlow,
        beginMicroTransfer,
        step: state.step,
        verificationMethod: state.method,
        error: state.error,
        errorKind: state.errorKind,
        messageToSign: state.messageToSign,
        isActive: state.step !== null,
        proof: state.proof,
        retry,
        tryMicroTransfer,
        confirmMicroTransfer,
        cancelCostDisclosure,
        cancel
    };
}
